import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MagnifyingGlassIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { chemicalInventoryApi } from "../../../api/chemical-inventory";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceEmpty } from "../../utils/ResourceEmpty";
import { ResourceError } from "../../utils/ResourceError";
import { PMDialog } from "../../utils/PMDialog";
import { ChemicalInventoryReviewBadge } from "./chemical-inventory-review-badge";
import { HazardSymbolsDisplay } from "./hazard-symbols-selector";
import {
  chemicalInventoryDocumentUrl,
  formatReviewLine,
  isSdsDocument,
} from "../../../utils/chemicalInventoryAccess";

export function HandbookChemicalInventoryTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await chemicalInventoryApi.getChemicalInventories({
          search,
          per_page: 100,
          status: "active",
        });
        if (!cancelled) setItems(response?.data || []);
      } catch {
        if (!cancelled) {
          setItems([]);
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search]);

  const openChemical = async (chemical) => {
    setSelected(chemical);
    setDetailLoading(true);
    try {
      const response = await chemicalInventoryApi.getChemicalInventory(chemical.id);
      if (response && !response.errors) {
        setSelected(response.data || response);
      }
    } catch {
      // Keep list row if show fails — SDS links may be missing.
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading && items.length === 0) {
    return <ResourceLoading size="base" />;
  }

  if (error && items.length === 0) {
    return (
      <ResourceError errors={[t("chemical.inventory.load_error")]} />
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("chemical.inventory.search")}
          className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm"
        />
      </div>

      {items.length === 0 ? (
        <ResourceEmpty message={t("chemical.inventory.no_results")} />
      ) : (
        <ul className="divide-y divide-gray-200 rounded-xl border border-gray-200">
          {items.map((chemical) => (
            <li key={chemical.id}>
              <button
                type="button"
                onClick={() => openChemical(chemical)}
                className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">
                    {chemical.name}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {t("chemical.inventory.cas_number")}: {chemical.cas_number || "—"}
                    {" · "}
                    {formatReviewLine(chemical, t)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <ChemicalInventoryReviewBadge reviewState={chemical.review_state} />
                  {chemical.hms_level ? (
                    <span className="text-[11px] text-slate-500">
                      {t(`chemical.inventory.hms_level.${chemical.hms_level}`)}
                    </span>
                  ) : null}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <HandbookChemicalDetailDialog
        chemical={selected}
        loading={detailLoading}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function HandbookChemicalDetailDialog({ chemical, loading, onClose }) {
  const { t } = useTranslation();
  if (!chemical) return null;

  const docs = chemical.chemical_inventory_documents || [];
  const sdsDocs = docs.filter(isSdsDocument);
  const downloadDocs = sdsDocs.length > 0 ? sdsDocs : docs;

  return (
    <PMDialog
      open={Boolean(chemical)}
      onClose={onClose}
      title={chemical.name}
    >
      <div className="space-y-4 text-sm">
        {loading ? <ResourceLoading size="small" /> : null}
        <p className="text-slate-600">{formatReviewLine(chemical, t)}</p>
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <dt className="font-medium text-slate-500">
              {t("chemical.inventory.cas_number")}
            </dt>
            <dd>{chemical.cas_number || "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">
              {t("chemical.inventory.hms_level")}
            </dt>
            <dd>
              {chemical.hms_level
                ? t(`chemical.inventory.hms_level.${chemical.hms_level}`)
                : "—"}
            </dd>
          </div>
        </dl>
        <div>
          <p className="mb-2 font-medium text-slate-500">
            {t("chemical.inventory.hazard_symbols")}
          </p>
          <HazardSymbolsDisplay symbols={chemical.hazard_symbols} />
        </div>
        <div>
          <p className="mb-1 font-medium text-slate-500">
            {t("chemical.inventory.first_aid")}
          </p>
          <p className="whitespace-pre-wrap text-slate-800">
            {chemical.first_aid || t("chemical.inventory.not_specified")}
          </p>
        </div>
        <div className="space-y-2">
          <p className="font-medium text-slate-500">
            {t("chemical.inventory.sds_download")}
          </p>
          {downloadDocs.length === 0 ? (
            <p className="text-slate-500">{t("chemical.inventory.sds_missing")}</p>
          ) : (
            downloadDocs.map((doc, index) => {
              const url = chemicalInventoryDocumentUrl(doc);
              const label =
                doc.document?.filename ||
                doc.document?.name ||
                doc.name ||
                t("chemical.inventory.document_type.safety_data_sheet");
              return url ? (
                <a
                  key={doc.id || index}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {label}
                </a>
              ) : (
                <p key={doc.id || index} className="text-slate-500">
                  {label}
                </p>
              );
            })
          )}
        </div>
      </div>
    </PMDialog>
  );
}
